import { Helmet } from 'react-helmet-async';
import { type MouseEvent, useEffect, useState, useCallback } from 'react';
import { Box, Button, Card, Stack, LinearProgress, Typography, Grid, Select, MenuItem } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
	useLazyGetRequestQuery,
	usePayRequestMutation,
	useGeneratePrebillingInvoiceMutation,
	useGetRequestReportsQuery,
	useLazyGetRequestReportQuery,
	useUploadRequestReportMutation,
	useDeleteRequestReportMutation,
	useSetRequestAdminNoteMutation,
	useSetRequestAccountNoteMutation,
	useUpdateRequestAttendanceStatusMutation,
	useGetRequestPaymentSummaryQuery,
} from '../../../slices/apis/requestApi';
import type { GetRequestsResponseData } from '../../../slices/apis/apis.types';

import Iconify from '../../../components/iconify';
import SuccessAlert from '../../../components/alert';
import Toast from '../../../components/toast';
import InfoCard from '../../../components/info-card';
import {
	Note,
	TestReview,
	Candidate,
	Invoices,
	AssessmentDetail,
	RequestReports,
	TotalSummary,
	Questionnaire,
} from '../../../components/shared';
import { Status, AttendanceStatus } from '../../../components/request';
import { BreadCrumbs } from '../../../components/nav-section';
import { Test, BulkRequest, attendanceStatusLabel } from '../../../models';
import { getFileMime } from '../../../utils/config';
import { omit } from '../../../utils/helpers';
import { fDateTime } from '../../../utils/formatTime';
import { useRole } from '../../../hooks/useRole';
import { usePermissions } from '../../../hooks/usePermissions';
import { useRequestRules } from '../../../hooks/useRequestRules';

const RequestView = () => {
	const { isUser, isSupplier, isContractor } = useRole();
	const { can } = usePermissions();
	const { reqId } = useParams() as { reqId: string };
	const [searchParam] = useSearchParams();
	const navigate = useNavigate();
	const [paymentSuccessAlertOpen, setPaymentSuccessAlertOpen] = useState(false);
	const [invoiceSentAlertOpen, setInvoiceSentAlertOpen] = useState(false);

	const [payRequest, { isSuccess, data }] = usePayRequestMutation();
	const [
		generatePrebillingInvoice,
		{ isLoading: isGeneratingInvoice, isSuccess: isInvoiceSuccess, isError: isInvoiceError, data: invoiceData },
	] = useGeneratePrebillingInvoiceMutation();
	const [setRequestAdminNote, { isSuccess: isUpdateAdminNoteSuccess, isError: isUpdateAdminNoteError }] =
		useSetRequestAdminNoteMutation();
	const [setRequestAccountNote, { isSuccess: isUpdateAccountNoteSuccess, isError: isUpdateAccountNoteError }] =
		useSetRequestAccountNoteMutation();
	const [
		updateRequestAttendanceStatus,
		{ isSuccess: isUpdateAttendanceStatusSuccess, isError: isUpdateAttendanceStatusError },
	] = useUpdateRequestAttendanceStatusMutation();
	const [trigger, result] = useLazyGetRequestQuery();

	const { data: paymentSummary } = useGetRequestPaymentSummaryQuery(reqId, { skip: !can('req:read:payment') });
	const { allowPayment } = useRequestRules({ data: result.data, paymentSummary });

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const handleSuccessAlertClose = useCallback(() => {
		setPaymentSuccessAlertOpen(false);
	}, []);

	const handleInvoiceSentAlertClose = useCallback(() => {
		setInvoiceSentAlertOpen(false);
	}, []);

	const handleAdminNoteSave = (note: string) => {
		setRequestAdminNote({
			id: reqId,
			note,
		});
	};

	const handleAccountNoteSave = (note: string) => {
		setRequestAccountNote({
			id: reqId,
			note,
		});
	};

	const handleAttendanceStatusChange = (attendanceStatus: GetRequestsResponseData['attendanceStatus']) => {
		updateRequestAttendanceStatus({
			id: reqId,
			attendanceStatus,
		});
	};

	useEffect(() => {
		if (isSuccess && data) {
			window.location.href = data.url;
		}
	}, [isSuccess, data]);

	useEffect(() => {
		if (isInvoiceSuccess && invoiceData?.invoiceEmailStatus === 'SENT') {
			setInvoiceSentAlertOpen(true);
		}
	}, [isInvoiceSuccess, invoiceData]);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (searchParam.get('from') === 'payment_success') {
			timer = setTimeout(() => {
				console.log('lazy load after 5 seconds');
				setPaymentSuccessAlertOpen(true);
				trigger(reqId);
			}, 5000);
		} else {
			trigger(reqId);
		}

		return () => clearTimeout(timer);
	}, [reqId, trigger, searchParam]);

	const onPayRequest = () => {
		payRequest({
			requestId: request.id,
			items: tests.map((test) => ({ id: test.id })),
		});
	};

	const onGeneratePrebillingInvoice = () => {
		generatePrebillingInvoice(request.id);
	};

	if (result.isLoading || !result.data || (can('req:read:payment') && !paymentSummary)) {
		return <LinearProgress />;
	}

	const request = result.data as GetRequestsResponseData;
	const { tests } = request;

	const scheduleDateLineText = BulkRequest.isAssessmentDatesSet(request.assessmentDates) ? 'Scheduled' : 'Preferred';
	const scheduleDates = BulkRequest.isAssessmentDatesSet(request.assessmentDates)
		? request.assessmentDates
		: request.prefAssessmentDates;

	const breadCrumbItems = [
		{
			text: 'My Single Requests',
			onClick: handleNavigate,
			dataRoute: '/dashboard/requests',
		},
		{
			text: `Request ${request.id}`,
		},
	];

	const assessmentDateSubHeader = (
		<Box sx={{ mb: 4 }}>
			<Stack spacing={1} alignItems="flex-start">
				<Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<BreadCrumbs items={breadCrumbItems} />
					{!isSupplier && allowPayment && (
						<Stack direction="row" spacing={1}>
							{can('req:invoice') && (
								<Button loading={isGeneratingInvoice} onClick={onGeneratePrebillingInvoice} variant="contained">
									<span>Send Payable Invoice</span>
								</Button>
							)}
							<Button disabled={isSuccess} onClick={onPayRequest} color="brand" variant="contained">
								<span>Pay Now</span>
							</Button>
						</Stack>
					)}
				</Box>
				<Status status={request.status} />
			</Stack>
		</Box>
	);

	const requestAttendanceStatus =
		request.attendanceStatus &&
		(isUser ? (
			<AttendanceStatus attendanceStatus={request.attendanceStatus} />
		) : (
			<Select
				size="small"
				disabled={!can('req:update:atdStatus')}
				value={request.attendanceStatus}
				onChange={(e) => handleAttendanceStatusChange(e.target.value as GetRequestsResponseData['attendanceStatus'])}
			>
				{Object.entries(attendanceStatusLabel).map(([value, label]) => (
					<MenuItem key={value} value={value}>
						{label}
					</MenuItem>
				))}
			</Select>
		));

	return (
		<>
			<Helmet>
				<title> Request Detail - {process.env.REACT_APP_ORG} </title>
			</Helmet>

			{isUpdateAdminNoteSuccess && (
				<Toast body={'Admin note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAdminNoteError && (
				<Toast body={'Admin note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteSuccess && (
				<Toast body={'Account note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteError && (
				<Toast body={'Account note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{isUpdateAttendanceStatusSuccess && (
				<Toast body={'Attendance status has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAttendanceStatusError && (
				<Toast body={'Attendance status failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{(isInvoiceError || (isInvoiceSuccess && invoiceData?.invoiceEmailStatus === 'FAILED')) && (
				<Toast body={'Failed to send payable invoice email.'} severity="error" autoHideDuration={3000} />
			)}

			<SuccessAlert
				open={paymentSuccessAlertOpen}
				title={'Thank you for your payment!'}
				subtitle={
					'Note your appointment is NOT CONFIRMED until you receive a confirmation email with your scheduled time.'
				}
				onClose={handleSuccessAlertClose}
			/>

			<SuccessAlert
				open={invoiceSentAlertOpen}
				title={'Invoice email sent'}
				subtitle={'Payable invoice has been emailed to the requesting user.'}
				onClose={handleInvoiceSentAlertClose}
			/>

			<Box sx={{ p: 2 }}>
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 6 }}>
						<Stack spacing={3}>
							<InfoCard title="Assessment Details" header={assessmentDateSubHeader}>
								<AssessmentDetail
									assessmentDates={scheduleDates}
									location={request.supplier.address}
									dateLineText={scheduleDateLineText}
								/>
								{request.status.toLowerCase() === 'cancelled' && (
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Iconify icon="eva:alert-triangle-outline" sx={{ color: 'error.main' }} />
										<Typography color="error">
											This request has been cancelled at {fDateTime(request.updatedAt)}
										</Typography>
									</Stack>
								)}
							</InfoCard>
							<InfoCard title="Candidate Details">
								<Candidate
									candidate={result.data.candidate}
									requestExtraInfo={result.data.extraInfo}
									requestAttendanceStatus={requestAttendanceStatus}
								/>
							</InfoCard>
							<InfoCard title="Item Details">
								<TestReview
									tests={tests.map((test) => {
										const normalised = Test.normalise(test);

										if (isSupplier || isContractor) {
											return omit(normalised, ['price']);
										}

										return normalised;
									})}
								/>
							</InfoCard>
						</Stack>
					</Grid>
					<Grid size={{ xs: 12, md: 6 }}>
						<Stack spacing={3}>
							{paymentSummary?.paymentStatus.toLowerCase() === 'fully_paid' && (
								<InfoCard title="Summary">
									<TotalSummary requestId={reqId} />
								</InfoCard>
							)}
							{paymentSummary && paymentSummary?.transactions.length > 0 && (
								<InfoCard title="Invoices">
									<Invoices transactions={paymentSummary.transactions} />
								</InfoCard>
							)}
							<Questionnaire requestId={reqId} />
							{request.id && (
								<Card variant="outlined">
									<RequestReports
										requestId={Number(request.id)}
										apis={{
											useGetRequestReportsQuery,
											useLazyGetRequestReportQuery,
											useUploadRequestReportMutation,
											useDeleteRequestReportMutation,
										}}
										mimeTypes={getFileMime()}
									/>
								</Card>
							)}
							{can('req:note:read') && (
								<InfoCard title="Admin Note">
									<Note
										title="Admin Note"
										val={result.data.adminNote}
										onSave={can('req:update:notes') ? handleAdminNoteSave : undefined}
									/>
								</InfoCard>
							)}
							{can('req:note:read') && (
								<InfoCard title="Account Note">
									<Note
										title="Account Note"
										val={result.data.accountNote}
										onSave={can('req:update:notes') ? handleAccountNoteSave : undefined}
									/>
								</InfoCard>
							)}
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default RequestView;
